import React from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { parseHtml } from '../../helpers/htmlParse'
import dayjs from 'dayjs'

import { isMobile as isMobileDevice } from 'react-device-detect'
import _ from 'lodash'
import classNames from 'classnames'
import { Table, Divider, Tag, Button, Typography, Card, List, Checkbox, Grid, Space } from 'antd'
import { CreditCardOutlined, PrinterOutlined } from '@ant-design/icons'

import Image from '../Image'
import ImagePreviewContainer from '../ImagePreview/ImagePreviewContainer'

import supplierOrderActions from '../../redux/supplierOrders/actions'
import notificationActions from '../../redux/notifications/actions'

const {
    changeStateTo,
	fetchSupplierOrderPDF
} = supplierOrderActions

const { setNotifications } = notificationActions

const SupplierOrderTable = ({
	toggleSupplierOrderForm,
	totalPages,
	supplierOrders,
	loading,
	pageSize,
	page,
	handleTableChange,
	setSelectedRows
}) => {
	const { i18n } = useTranslation(['suppliers'])
	const dispatch = useDispatch()
	const screens = Grid.useBreakpoint()
	const isCompact = !screens.md
	const [selectedKeys, setSelectedKeys] = React.useState([])

	const toggleSelected = (id, checked) => {
		const next = checked
			? _.uniq([...selectedKeys, id])
			: selectedKeys.filter((k) => k !== id)
		setSelectedKeys(next)
		setSelectedRows(supplierOrders.filter((row) => next.includes(row.id)))
	}

	const renderTruckReservationCost = (truckReservationCost, order) => {
		const isSelfPickup = order.shippingType === 'self_pickup'
		const shippingCost = truckReservationCost === '0.00' ?
			i18n.t('suppliers:supplierOrder.n/a') : truckReservationCost

		if (isSelfPickup) {
			return i18n.t('suppliers:selfPickup')
		} else {
			return shippingCost
		}
	}

	const renderRvYield = (order) => {
		const rvVat = _.get(order, 'orderYield.rv.display', null) ?
			_.get(order, 'orderYield.rv.display') :
			i18n.t('suppliers:supplierOrder.n/a')

		return rvVat
	}

	const renderSupplierNewTag = (supplier = {}) => {
        if (_.isEmpty(supplier) || supplier.bankName && supplier.bankAccountNumber) { return null }

        return <Tag color="gold">NEW</Tag>
    }

	const renderImage = (dataIndex) => {
        if (!dataIndex || !dataIndex.length) return null
        return dataIndex.map((image) => {
            return (
								<div className='image-container'>
									<Image key={`attached_file_${image.id}`}
										contentType={'image/jpeg'}
										originalUrl={_.get(image, 'file.original', null)}
										thumbUrl={_.get(image, 'file.small', null)}
										isImageBlob={false} />
								</div>
						)
				})
    }

		const renderHtmlString = (htmlString, isTransfer) => {
        const text = parseHtml(htmlString)

        return (
            <div>
                <span style={{ fontWeight: 'bold' }}>
                    { isTransfer ? i18n.t('suppliers:transferForm.note') : 'Note' }:
                </span>
                <span>{text}</span>
            </div>
        )
    }

	const renderSlip = (dataIndex) => {
        if (!dataIndex || !dataIndex.length) return null
        return dataIndex.map((image, index) => {
            const noticeText = [
                "เอกสารใบกำกับภาษี สำเนา ส่งมาทางไลน์",
                "เอกสารใบกำกับภาษี ตัวจริง ส่งมาที่ สนง.ใหญ่สีลมเท่านั้น",
                "เอกสารให้คนรถ ต้องไม่มีราคา"
            ]

            return (
				<div className='image-container'>
					<Image key={_.get(image, 'id', `slip-${index}`)}
						contentType={'image/jpeg'}
						originalUrl={_.get(image, 'file.original', null)}
						thumbUrl={_.get(image, 'file.small', null)}
						isImageBlob={false}
						noticeText={noticeText} />
				</div>
            )
        })
    }

	const renderDownloadPDF = (rows) => {
        return (
            <a onClick={() => dispatch(fetchSupplierOrderPDF(rows.id))}>
				<PrinterOutlined />
            </a>
        )
    }

	const renderChangeStateToChosenByOshBtn = (supplierOrder) => {
        const id = _.get(supplierOrder, 'id', '')
        return (
            <Button
                onClick={() => dispatch(changeStateTo({
                    state: 'chosen_by_osh',
                    id,
                    onSuccess: () => { dispatch(setNotifications('success', 'success', 'success')) },
                    onError: () => { dispatch(setNotifications('error', 'error', 'error')) }
                }))}
                size="small"
                type="primary"
            >
                {i18n.t('suppliers:supplierOrder.orderState.chosenByOsh')}
            </Button>
        )
    }

	const supplierOrderColumns = () => {
		return (
			[
				{
					title: i18n.t('suppliers:supplierOrder.shippingCostTotal'),
					width: 100,
					key: 'truck-reservation-costs-sum',
					dataIndex: 'displayTruckReservationCostsSum',
					render: (truckReservationCostSum, row) => {
						return (
							<div>
								{renderTruckReservationCost(truckReservationCostSum, row.order)}
							</div>
						)
					}
				},
				{
					title: i18n.t('suppliers:supplierOrder.rv'),
					width: 100,
					key: 'rv-vat',
					render: (row) => {
						return (
							<div>
								{renderRvYield(row.order)}
							</div>
						)
					}
				},
				{
					title: `${i18n.t('suppliers:supplierOrder.refCode')} / Link`,
					width: 180,
					key: 'ref-code',
					render: (value, rows) => {
						const order = value.order
						return (
							<div>
								<a key={rows.id}
									onClick={() => toggleSupplierOrderForm({
										id: rows.id,
										orderId: rows.order.number,
										supplierOrderType: rows.supplierOrderType
										})}>
									{value.refCode}
								</a>
								<Divider style={{ margin: '4px' }}/>
								<div>
									<Link key={'order'} to={`/inquiries/${order.number}`}>
										{ order.number }
									</Link>
								</div>
								<div>
									<Link to={`/orders/${order.number}/accountings`} target="_blank"
										key={'accounting'}>
										<CreditCardOutlined style={{ margin: '0 3px' }} />
										Accounting
									</Link>
								</div>
							</div>
						)
					}
				},
				{
					title: i18n.t('suppliers:supplierName'),
					width: 150,
					key: 'supplier',
					render: (record) => {
						const order = record.order
						const doNotTransfer = order.doNotTransfer
						const plateNumberList = record.trucks.map((truck) => {
							return truck.licensePlateNumber
						}).join(', ')

						const supplier = record.supplier
						const pv = (
							<div>
								<span style={{ fontWeight: 'bold' }}>
									{i18n.t('suppliers:supplierOrder.withHoldingTax3Per')}:
								</span>
								<span>
									{_.get(record, 'paymentVoucher.displayAmount', '0.00')}
								</span>
							</div>
						)

						return (
							<div>
								<div style={_.get(supplier, 'isVerified') ? { color: 'red' } : {}}>
									{record.supplierName} {renderSupplierNewTag(supplier)}
								</div>
								{record.paymentType ?
									<div>({i18n.t(`suppliers:supplierOrder.${_.camelCase(record.paymentType)}`)})</div> :
									null}
								{plateNumberList ? <div>[{plateNumberList}]</div> : null}
								{record.isAvailable ? <Tag color="red">{i18n.t('suppliers:quickTransfer')}</Tag> : null}
								{doNotTransfer ? <Tag color="red">{i18n.t('suppliers:doNotTransfer')}</Tag> : null}
								{_.get(supplier, 'conditionNote') ?
									<div>
										<span style={{ fontWeight: 'bold' }}>{i18n.t('suppliers:conditionNote')}: </span>
										<span>
											{_.get(supplier, 'conditionNote')}
										</span>
									</div> : null
								}
								<div>
									<span style={{ fontWeight: 'bold' }}>{i18n.t('suppliers/overTransferAccounts:balance')}: </span>
									<span>
										{_.get(supplier, 'supplierOverTransferAccountBalance.display', '0.00')}
									</span>
								</div>
								{record.supplierOrderType === 'profit_sharing' && !_.isEmpty(record.paymentVoucher) && pv}
							</div>
						)
					}
				},
				{
					title: i18n.t('suppliers:supplierOrder.attachment'),
					width: 100,
					key: 'attachments',
					dataIndex: 'images',
					render: (dataIndex, row) => {
						const whoRequestForPayment = row.whoRequestForPaymentFirstName
						const type = row.supplierOrderType
						const isTransfer = ['transfer_back', 'miscellaneous_transfer'].includes(type)

						return (
							<div className="image-container">
								{renderImage(dataIndex)}
								{ whoRequestForPayment ? <div>({whoRequestForPayment})</div> : null }
								{renderHtmlString(row.note, isTransfer)}
							</div>
						)
					}
				},
				{
					title: i18n.t('suppliers:supplierOrder.slip'),
					width: 100,
					key: 'slips',
					dataIndex: ['paymentVoucher', 'images'],
					render: (dataIndex) => (
						<div className="image-container">
							{renderSlip(dataIndex)}
						</div>
					)
				},
				{
					title: i18n.t('suppliers:supplierLineItems.pvAmount'),
					width: 100,
					key: 'pv-amount',
					render: (values) => {
						const grandTotalAfterWithdraw = _.get(values, 'grandTotalAfterWithdraw.raw')
						const grandTotal = _.toNumber(_.get(values, 'grandTotal.raw'))
						const paymentVoucherAmount = _.get(values, 'paymentVoucher.amount.display', '')
						const supplierOrderGrandTotal = _.get(values, 'grandTotal.display', '')

						const order = _.get(values, 'order', '')
						const yieldValue = _.toNumber(_.get(order, 'orderYield.yield.raw', 0))

						return (
							<div className={classNames({ paymentVoucher: supplierOrderGrandTotal !== paymentVoucherAmount })}>
								{yieldValue < 0 && <span>(-) </span>}
								<Typography.Text disabled={grandTotalAfterWithdraw}>
									{paymentVoucherAmount}
								</Typography.Text>
								{grandTotalAfterWithdraw ? (
									<Tag color={grandTotal > _.toNumber(grandTotalAfterWithdraw) ? 'red' : 'green'}>
										{_.get(values, 'grandTotalAfterWithdraw.display')}
									</Tag>
								) : null}
								{_.get(values, 'sumDepositAmount.raw') === '0' ? null : (
									<div>
										[{i18n.t('suppliers:supplierOrder.deposit')} {_.get(values, 'sumDepositAmount.display')}]
									</div>
								)}
								{_.get(values, 'sumWithdrawAmount.raw') === '0' ? null : (
									<div>
										[{i18n.t('suppliers:supplierOrder.withdraw')} {_.get(values, 'sumWithdrawAmount.display')}]
									</div>
								)}
							</div>
						)
					}
				},
				{
					title: i18n.t('suppliers:supplierOrder.transferDate'),
					width: 120,
					key: 'transfer-date',
					render: (row) => {
						const transferDate = _.get(row, 'paymentVoucher.transferDate', null)
						const dateOfPurchase = _.get(row, 'paymentVoucher.dateOfPurchase', null)

						return (
							<div>
								<div>
									{transferDate ?
										dayjs(transferDate, 'YYYY-MM-DD').format('D MMM YYYY') : '-'}
								</div>
								<div>
									({dateOfPurchase ?
										dayjs(dateOfPurchase, 'YYYY-MM-DD').format('D MMM YYYY') : '-'})
								</div>
							</div>
						)
					}
				},
				{
					title: i18n.t('suppliers:supplierOrder.shippingDate'),
					width: 120,
					key: 'shipping-date',
					defaultSortOrder: 'descend',
					render: (row) => {
						return (
							<div>
								<div>
									{row.shippingDate}
								</div>
							</div>
						)
					}
				},
				{
					key: 'actions',
					width: 150,
					render: (rows) => {
						return (
							<div className="actions">
								{renderDownloadPDF(rows)}

								{(_.get(rows, 'state', '') === 'need_accounting_review') &&
									<React.Fragment>
										<Divider type="vertical" />
										{renderChangeStateToChosenByOshBtn(rows)}
									</React.Fragment>
								}
							</div>
						)
					}
				},
				{
					key: 'is-exported',
					width: 150,
					render: (row) => {
						const isExported = _.get(row, 'paymentVoucher.isExported', null)
						return (
							<div>
								{isExported ? i18n.t('suppliers:supplierOrder.isExported') : ''}
							</div>
						)
					}
				}
			]
		)
	}

	const renderRowSelection = () => {
			const rowSelection = {
					onChange: (selectedRowKeys, selectedRows) => {
						setSelectedRows(selectedRows)
					}
			}

			return rowSelection
	}

	const pagination = {
        current: page,
        pageSize: pageSize,
        total: pageSize * totalPages,
				showSizeChanger: false
    }

	const renderMobileCard = (row) => {
		const order = row.order || {}
		const supplier = row.supplier || {}
		const isExported = _.get(row, 'paymentVoucher.isExported', null)
		const paymentVoucherAmount = _.get(row, 'paymentVoucher.amount.display', '')
		const supplierOrderGrandTotal = _.get(row, 'grandTotal.display', '')
		const transferDate = _.get(row, 'paymentVoucher.transferDate', null)
		const plateNumberList = (row.trucks || []).map((t) => t.licensePlateNumber).join(', ')
		const checked = selectedKeys.includes(row.id)
		const type = row.supplierOrderType
		const isTransfer = ['transfer_back', 'miscellaneous_transfer'].includes(type)
		const supplierImages = row.images || []
		const slipImages = _.get(row, 'paymentVoucher.images', [])

		return (
			<Card size="small" className="supplier-order-card" style={{ marginBottom: 8 }}>
				<div className="card-row card-header">
					<Checkbox
						checked={checked}
						onChange={(e) => toggleSelected(row.id, e.target.checked)}
					/>
					<a
						className="ref-code"
						onClick={() => toggleSupplierOrderForm({
							id: row.id,
							orderId: order.number,
							supplierOrderType: row.supplierOrderType
						})}
					>
						{row.refCode}
					</a>
					{renderDownloadPDF(row)}
				</div>

				<div className="card-row">
					<Link to={`/inquiries/${order.number}`}>{order.number}</Link>
					{' · '}
					<Link to={`/orders/${order.number}/accountings`} target="_blank">
						<CreditCardOutlined /> Accounting
					</Link>
				</div>

				<div className="card-row">
					<div style={_.get(supplier, 'isVerified') ? { color: 'red' } : {}}>
						<strong>{row.supplierName}</strong> {renderSupplierNewTag(supplier)}
					</div>
					<Space size={4} wrap>
						{row.isAvailable ? (
							<Tag color="red">{i18n.t('suppliers:quickTransfer')}</Tag>
						) : null}
						{order.doNotTransfer ? (
							<Tag color="red">{i18n.t('suppliers:doNotTransfer')}</Tag>
						) : null}
						{row.paymentType ? (
							<Tag>{i18n.t(`suppliers:supplierOrder.${_.camelCase(row.paymentType)}`)}</Tag>
						) : null}
						{plateNumberList ? <Tag>{plateNumberList}</Tag> : null}
					</Space>
				</div>

				<div className="card-row card-stats">
					<span>
						<Typography.Text type="secondary">
							{i18n.t('suppliers:supplierLineItems.pvAmount')}:{' '}
						</Typography.Text>
						<strong className={classNames({ paymentVoucher: supplierOrderGrandTotal !== paymentVoucherAmount })}>
							{paymentVoucherAmount || '-'}
						</strong>
					</span>
					<span>
						<Typography.Text type="secondary">
							{i18n.t('suppliers:supplierOrder.shippingCostTotal')}:{' '}
						</Typography.Text>
						{renderTruckReservationCost(row.displayTruckReservationCostsSum, order)}
					</span>
					<span>
						<Typography.Text type="secondary">
							{i18n.t('suppliers:supplierOrder.rv')}:{' '}
						</Typography.Text>
						{renderRvYield(order)}
					</span>
				</div>

				<div className="card-row card-stats">
					<span>
						<Typography.Text type="secondary">
							{i18n.t('suppliers:supplierOrder.transferDate')}:{' '}
						</Typography.Text>
						{transferDate ? dayjs(transferDate, 'YYYY-MM-DD').format('D MMM YYYY') : '-'}
					</span>
					<span>
						<Typography.Text type="secondary">
							{i18n.t('suppliers:supplierOrder.shippingDate')}:{' '}
						</Typography.Text>
						{row.shippingDate || '-'}
					</span>
				</div>

				{(supplierImages.length || slipImages.length) ? (
					<div className="card-row">
						{supplierImages.length ? (
							<div className="image-container">
								{renderImage(supplierImages)}
								{row.note ? renderHtmlString(row.note, isTransfer) : null}
							</div>
						) : null}
						{slipImages.length ? (
							<div className="image-container">
								<Typography.Text type="secondary">
									{i18n.t('suppliers:supplierOrder.slip')}:
								</Typography.Text>
								{renderSlip(slipImages)}
							</div>
						) : null}
					</div>
				) : null}

				{isExported ? (
					<div className="card-row">
						<Tag color="green">{i18n.t('suppliers:supplierOrder.isExported')}</Tag>
					</div>
				) : null}

				{_.get(row, 'state', '') === 'need_accounting_review' ? (
					<div className="card-row">
						{renderChangeStateToChosenByOshBtn(row)}
					</div>
				) : null}
			</Card>
		)
	}

	if (isCompact) {
		return (
			<List
				className="supplier-order-mobile-list"
				loading={loading}
				dataSource={supplierOrders}
				rowKey={(row) => row.id}
				pagination={{
					...pagination,
					onChange: (current) => handleTableChange({ current }, {}, {})
				}}
				renderItem={(row) => (
					<List.Item style={{ padding: 0, border: 'none' }}>
						{renderMobileCard(row)}
					</List.Item>
				)}
			/>
		)
	}

	return (
		<Table columns={supplierOrderColumns()}
			scroll={isMobileDevice ? { x: 1300 } : { x: 1000 }}
			rowKey={supplierOrder => supplierOrder.id}
			dataSource={supplierOrders}
			loading={loading}
			pagination={pagination}
			onChange={handleTableChange}
			rowSelection={renderRowSelection()}
		/>
	)
}

SupplierOrderTable.propTypes = {
	toggleSupplierOrderForm: PropTypes.func.isRequired,
	handleTableChange: PropTypes.func.isRequired,
	setSelectedRows: PropTypes.func,
	totalPages: PropTypes.number,
	supplierOrders: PropTypes.array,
	loading: PropTypes.bool,
	pageSize: PropTypes.number,
	page: PropTypes.number
}

SupplierOrderTable.defaultProps = {
	totalPages: 1,
	page: 1,
	supplierOrders: [],
	pageSize: 20,
	setSelectedRows: () => {}
}

export default ImagePreviewContainer(SupplierOrderTable)
